import { useCallback, useMemo, useState } from "react";
import { AskBar } from "@/components/AskBar";
import { ChatFeed } from "@/components/ChatFeed";
import { HealthBadge } from "@/components/HealthBadge";
import { SearchBar, type SearchSelectContext } from "@/components/SearchBar";
import { SearchNavigator } from "@/components/SearchNavigator";
import { useFeed } from "@/hooks/useFeed";
import { useLiveChat } from "@/hooks/useLiveChat";
import { useSearch } from "@/hooks/useSearch";
import type { SearchIndexEntry } from "@/types";
import { buildFeedRows, mergeById } from "@/utils/feedRows";
import { messageIdKey } from "@/utils/messageId";
import { sortFeedMessages } from "@/utils/feedSort";
import {
  findSearchResultIndex,
  sortSearchResultsChrono,
} from "@/utils/searchResults";

const APP_TITLE =
  import.meta.env.VITE_APP_TITLE ||
  "Чат по маркировке Честный Знак: ответы на вопросы в реальном времени";

export default function ChatPage() {
  const {
    messages: archiveMessages,
    byId: archiveById,
    loading,
    loadingMore,
    error,
    hasMore,
    hasMoreNewer,
    atLatest,
    loadOlder,
    loadNewer,
    jumpToLatest,
    firstItemIndex,
    jumpToMessage,
  } = useFeed();
  const {
    liveMessages,
    loading: askLoading,
    error: askError,
    ask,
    scrollTick,
    userAuthor,
  } = useLiveChat();

  const search = useSearch();
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [focusMessageId, setFocusMessageId] = useState<string | null>(null);
  const [scrollToEndTick, setScrollToEndTick] = useState(0);
  const [searchSession, setSearchSession] = useState<{
    results: SearchIndexEntry[];
    index: number;
    query: string;
  } | null>(null);

  const sortedSearchResults = useMemo(
    () => sortSearchResultsChrono(search.results),
    [search.results]
  );

  const allMessages = useMemo(() => {
    if (!atLatest) {
      return sortFeedMessages([...archiveMessages]);
    }
    return sortFeedMessages([...archiveMessages, ...liveMessages]);
  }, [archiveMessages, liveMessages, atLatest]);

  const byId = useMemo(
    () => mergeById(archiveById, liveMessages),
    [archiveById, liveMessages]
  );

  const rows = useMemo(() => buildFeedRows(allMessages), [allMessages]);

  const goToMessage = useCallback(
    async (id: string, hit?: SearchIndexEntry) => {
      const key = messageIdKey(id);
      setHighlightId(key);
      const fallback =
        hit?.text ?
          {
            id: key,
            date: hit.date,
            author: hit.author,
            text: hit.text,
            author_kind: "user" as const,
          }
        : undefined;
      const ok = await jumpToMessage(key, fallback);
      if (ok) {
        setFocusMessageId(key);
      } else {
        setHighlightId(null);
        setFocusMessageId(null);
      }
    },
    [jumpToMessage]
  );

  const handleSearchSelect = useCallback(
    async (
      messageId: string,
      hit: SearchIndexEntry,
      ctx: SearchSelectContext
    ) => {
      const sorted = sortSearchResultsChrono(ctx.results);
      const index = findSearchResultIndex(sorted, messageId);
      setSearchSession({
        results: sorted,
        index: index >= 0 ? index : 0,
        query: ctx.query,
      });
      await goToMessage(messageId, hit);
    },
    [goToMessage]
  );

  const navigateSearchHit = useCallback(
    async (delta: number) => {
      if (!searchSession) return;
      const next = searchSession.index + delta;
      if (next < 0 || next >= searchSession.results.length) return;
      const hit = searchSession.results[next];
      setSearchSession({ ...searchSession, index: next });
      await goToMessage(hit.id, hit);
    },
    [searchSession, goToMessage]
  );

  const handleJumpToLatest = useCallback(async () => {
    setHighlightId(null);
    setFocusMessageId(null);
    setSearchSession(null);
    await jumpToLatest();
    setScrollToEndTick((t) => t + 1);
  }, [jumpToLatest]);

  return (
    <div className="app-shell">
      <div className="app">
        <header className="app-header">
          <div className="app-brand">
            <div className="app-logo" aria-hidden>
              ЧЗ
            </div>
            <div className="app-header-text">
              <h1>{APP_TITLE}</h1>
            </div>
          </div>
          <div className="app-header-actions">
            <SearchBar
              query={search.query}
              onQueryChange={search.setQuery}
              results={sortedSearchResults}
              loading={search.loading}
              error={search.error}
              totalHint={search.totalHint}
              onSelect={handleSearchSelect}
            />
            <HealthBadge />
          </div>
        </header>

        <main className="app-main">
          <section className="feed-panel">
            <ChatFeed
              rows={rows}
              byId={byId}
              loading={loading}
              error={error}
              highlightMessageId={highlightId}
              focusMessageId={focusMessageId}
              scrollTick={scrollTick}
              scrollToEndTick={scrollToEndTick}
              onGoToMessage={goToMessage}
              firstItemIndex={firstItemIndex}
              hasMoreOlder={hasMore}
              hasMoreNewer={hasMoreNewer}
              atLatest={atLatest}
              loadingMore={loadingMore}
              onLoadOlder={loadOlder}
              onLoadNewer={loadNewer}
              onJumpToLatest={handleJumpToLatest}
            />
            {searchSession && searchSession.results.length > 1 && (
              <SearchNavigator
                index={searchSession.index}
                total={searchSession.results.length}
                onPrev={() => navigateSearchHit(-1)}
                onNext={() => navigateSearchHit(1)}
              />
            )}
            <AskBar
              onAsk={ask}
              loading={askLoading}
              error={askError}
              userAuthor={userAuthor}
            />
          </section>
        </main>
      </div>
    </div>
  );
}
